import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as FileSystem from "expo-file-system/legacy";
import { processImage } from "../features/Image/processImage";
import { Platform } from "react-native";
import { UploadContext } from "arkh/src/common/UploadContext";
import cuid from "cuid";

export type Upload =
  | {
      id?: string;
      status: "processing";
      localImageURIOriginal: string;
      width: number;
      height: number;
      uploadId: string;
    }
  | {
      id?: string;
      status: "upload-pending";
      localImageURI: string;
      localImageURIOriginal: string;
      width: number;
      height: number;
      uploadId: string;
    }
  | {
      id: string;
      status: "uploading";
      localImageURI: string;
      localImageURIOriginal: string;
      uploadURL: string;
      uploadId: string;
      width: number;
      height: number;
    }
  | {
      id: string;
      status: "done";
      localImageURI: string;
      localImageURIOriginal: string;
      width: number;
      height: number;
      uploadId: string;
    }
  | {
      id: string;
      status: "error";
      localImageURI: string;
      localImageURIOriginal: string;
      uploadURL: string;
      uploadId: string;
      width: number;
      height: number;
    };

export const ImageUploadProvider = (props: {
  children?: ReactNode | undefined;
}) => {
  const [uploads, setUploads] = useState<Upload[]>([]);

  const prepareForImageUpload = useCallback(
    async (args: {
      width: number;
      height: number;
      localImageURI: string;
      processImageActions?: Parameters<typeof processImage>[1];
      imageId?: string;
    }) => {
      const id = cuid();

      setUploads((prev) => [
        ...prev,
        {
          id: args.imageId,
          height: args.height,
          localImageURIOriginal: args.localImageURI,
          width: args.width,
          status: "processing",
          uploadId: id,
        },
      ]);

      const imgRes = await processImage(
        args.localImageURI,
        args.processImageActions
      );

      setUploads((prev) =>
        prev.map((upload) =>
          upload.status === "processing" && upload.uploadId === id
            ? {
                ...upload,
                status: "upload-pending",
                localImageURI: imgRes.uri,
                width: imgRes.width,
                height: imgRes.height,
              }
            : upload
        )
      );

      return id;
    },
    []
  );

  const uploadImage = useCallback(
    async ({
      imageId,
      uploadURL,
      uploadId,
    }: {
      imageId: string;
      uploadURL: string;
      uploadId: string;
    }) => {
      setUploads((prev) => {
        return prev.map((upload) =>
          upload.uploadId === uploadId && upload.status === "upload-pending"
            ? {
                ...upload,
                status: "uploading",
                id: imageId,
                uploadURL,
              }
            : upload
        );
      });
    },
    []
  );

  useEffect(() => {
    const uploadsToUpload = uploads.filter(
      (upload) => upload.status === "uploading"
    );

    uploadsToUpload.forEach(async (image) => {
      try {
        if (Platform.OS === "web") {
          const response = await fetch(image.localImageURI);
          const blob = await response.blob();

          const uploadResponse = await fetch(image.uploadURL, {
            method: "PUT",
            body: blob,
            headers: {
              "Content-Type": "image/jpeg",
            },
          });

          if (!uploadResponse.ok) {
            throw new Error(
              `Upload failed with status: ${uploadResponse.status}`
            );
          }
        } else {
          await FileSystem.uploadAsync(image.uploadURL, image.localImageURI, {
            httpMethod: "PUT",
            mimeType: "image/jpeg",
            sessionType: FileSystem.FileSystemSessionType.BACKGROUND,
          });
        }

        setUploads((prev) =>
          prev.map((prevU) =>
            prevU.uploadId === image.uploadId && prevU.status === "uploading"
              ? {
                  ...prevU,
                  status: "done",
                }
              : prevU
          )
        );
      } catch {
        setUploads((prev) =>
          prev.map((prevU) =>
            prevU.uploadId === image.uploadId && prevU.status === "uploading"
              ? { ...prevU, status: "error" }
              : prevU
          )
        );
      }
    });
  }, [uploads]);

  const value = useMemo(
    () => ({ uploadImage, uploads, prepareForImageUpload }),
    [uploadImage, uploads, prepareForImageUpload]
  );
  return (
    <UploadContext.Provider value={value}>
      {props.children}
    </UploadContext.Provider>
  );
};
